import { FC, useEffect, useState, useMemo, useContext } from "react";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import moment from "moment";

import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff";
import AirplanemodeInactiveIcon from "@mui/icons-material/AirplanemodeInactive";
import FlightIcon from "@mui/icons-material/Flight";
import SaveIcon from "@mui/icons-material/Save";

import {
  getComplianceWithCourses,
  ComplianceWithCoursesResponseHydrated as ComplianceWithCourses,
  CertificateMap,
  CourseCertificate,
} from "src/api/compliance";
import { getCrewUsers } from "src/api/crew";
import { getDefaultSettings, saveDefaultSettings } from "src/api/settings";
import { SettingKey } from "src/shared/api-types/user";
import { useCacheRefresh } from "src/hooks/useCacheRefresh";
import useCrew from "src/hooks/useCrew";
import type { UserCrew } from "src/shared/api-types/user";
import type { ComplianceStatus, ActionCourse, AircraftNode, RoleNode } from "src/shared/api-types/certificate";
import CrewCourseOverviewTable, {
  type AircraftType,
  type Course as TableCourse,
  type User as TableUser,
} from "src/components/CrewCourseOverviewTable";
import Spinner from "src/components/Spinner";
import CacheStatusBanner from "src/components/CacheStatusBanner";
import { formatDate, formatLastFirstName } from "src/services/formatters";
import { captureHandledException } from "src/services/sentry";
import courseCategoryService, { CourseCategoryType } from "src/services/courseCategory";
import { COMMON } from "src/constants";
import { CertificateStatusType } from "src/services/certificateStatus";
import { createSelectableObject } from "src/components/Form/types";
import CheckboxAutocomplete from "src/components/CheckboxAutocomplete";
import Button from "src/components/Button";
import DisplayContext from "src/context/DisplayContext";
import { takeScreenshot } from "src/utils/screenshot";

import { CachePrefix } from "src/shared/api-types/cache";
import { EmptyState } from "src/components/EmptyState";

import "./index.scss";

// Cache refresh configuration
const CACHE_REFRESH_CONFIG = {
  baseUrl: "/compliance",
  cachePrefix: CachePrefix.USER_COMPLIANCE_WITH_COURSES,
} as const;

interface CrewOverviewProps {
  match: {
    params: {
      crewId: string;
    };
  };
}

// Type definitions for internal data structures
// Re-use types from the table component to ensure compatibility
type CourseWithRoles = TableCourse;
type UserRow = TableUser;

interface CourseStatus {
  course_id: string | number;
  status: ComplianceStatus;
  validUntil: string | null;
  buffer: string | null;
  days: number | null;
  purchased: boolean;
  purchasableCourse?: ActionCourse;
  courseCertificate?: CourseCertificate;
}

interface UserWithCompliance {
  user: UserCrew;
  compliance: {
    data: ComplianceWithCourses;
    cachedAt: string | undefined;
  } | null;
}

interface ExtractedData {
  courses: CourseWithRoles[];
  // One group of columns per aircraft type, sorted by type name
  aircraftCourseGroups: CourseWithRoles[][];
  aircraftTypes: AircraftType[];
  roles: Array<{ id: number; name: string }>;
  operations: Array<{ id: number; name: string }>;
}

type AircraftCourseNode = Pick<AircraftNode, "id" | "name" | "courseIds">;

/**
 * Calculate remaining days until certificate expiration
 */
const calculateRemainingDays = (validPeriod: string | null): number | null => {
  if (!validPeriod) return null;

  const validDue = moment(validPeriod);
  return validDue.diff(moment(), "days") + 1;
};

/**
 * Calculate buffer zone start date (expiry date - buffer months)
 */
const calculateBufferZoneStartDate = (validPeriod: string | null, bufferZone: number | null): string | null => {
  if (!validPeriod || !bufferZone) return null;

  const validDue = moment(validPeriod);
  return formatDate(validDue.clone().subtract(bufferZone, "months"));
};

/**
 * All non-aircraft course IDs a role node requires: position-level, operation-level and role-level.
 * A role without positions carries its courses only in role.courseIds (the API copies role-level
 * courses into position.courseIds only when the user has a position under that role).
 */
const collectRoleCourseIds = (role: RoleNode): string[] => [
  ...(role.positions ?? []).flatMap((position) => [
    ...(position.courseIds ?? []),
    ...(position.operations ?? []).flatMap((operation) => operation.courseIds ?? []),
  ]),
  ...(role.courseIds ?? []),
];

/**
 * All aircraft nodes of a role node: role-, position- and operation-level. The API emits one node per
 * aircraft type assigned to the user, so a type the user isn't assigned never shows up here.
 */
const collectRoleAircraftNodes = (role: RoleNode): AircraftCourseNode[] => [
  ...(role.aircraft ?? []),
  ...(role.positions ?? []).flatMap((position) => [
    ...(position.aircraft ?? []),
    ...(position.operations ?? []).flatMap((operation) => operation.aircraft ?? []),
  ]),
];

/**
 * The aircraft-specific course IDs a user requires, per aircraft type ID. Contains every type the user is
 * assigned, also the ones without courses.
 */
const collectUserAircraftCourseIds = (roles: RoleNode[] = []): Map<number, Set<string>> => {
  const courseIdsByType = new Map<number, Set<string>>();
  roles.flatMap(collectRoleAircraftNodes).forEach((aircraft) => {
    const courseIds = courseIdsByType.get(aircraft.id) ?? new Set<string>();
    (aircraft.courseIds ?? []).forEach((courseId) => courseIds.add(courseId));
    courseIdsByType.set(aircraft.id, courseIds);
  });
  return courseIdsByType;
};

/**
 * Add a course column required by a role, or add the role to an existing column
 */
const addRoleCourse = (
  coursesMap: Map<string, CourseWithRoles>,
  certificateMap: CertificateMap,
  courseId: string,
  roleId: number,
  aircraftType?: AircraftType,
) => {
  const cert = certificateMap[courseId];
  if (!cert?.name) return;

  const existingCourse = coursesMap.get(courseId);
  if (!existingCourse) {
    coursesMap.set(courseId, {
      id: courseId,
      name: cert.name,
      fullname: cert.name,
      courseCategory: cert.courseCategory || COMMON.UNCATEGORIZED,
      roles: [{ role_id: roleId }],
      ...(aircraftType && { aircraftType }),
    });
  } else if (!existingCourse.roles.some((r) => r.role_id === roleId)) {
    // Course already exists, add this role if not already there
    existingCourse.roles.push({ role_id: roleId });
  }
};

const sortCoursesByName = (courses: CourseWithRoles[]) => courses.sort((a, b) => a.name.localeCompare(b.name));

/**
 * Extract all courses, roles, and operations from V2 compliance data
 * Single pass through all users' relationTrees
 */
const extractDataFromV2Compliance = (usersWithCompliance: UserWithCompliance[]): ExtractedData => {
  const allCoursesMap = new Map<string, CourseWithRoles>();
  const aircraftCoursesMap = new Map<number, { aircraftType: AircraftType; courses: Map<string, CourseWithRoles> }>();
  const allRolesMap = new Map<number, { id: number; name: string }>();
  const allOperationsMap = new Map<number, { id: number; name: string }>();

  usersWithCompliance.forEach(({ compliance }) => {
    if (!compliance?.data?.relationTree) return;

    const { certificateMap, relationTree } = compliance.data;

    relationTree.roles?.forEach((role) => {
      // Collect role
      allRolesMap.set(role.id, { id: role.id, name: role.name });

      // Collect operations
      role.positions?.forEach((position) => {
        position.operations?.forEach((operation) => {
          allOperationsMap.set(operation.id, { id: operation.id, name: operation.name });
        });
      });

      // Collect courses from role, position and operation level
      collectRoleCourseIds(role).forEach((courseId) => addRoleCourse(allCoursesMap, certificateMap, courseId, role.id));

      // Collect aircraft-specific courses per aircraft type, from role, position and operation level
      collectRoleAircraftNodes(role).forEach((aircraft) => {
        const aircraftCourses = aircraftCoursesMap.get(aircraft.id) ?? {
          aircraftType: { id: aircraft.id, name: aircraft.name },
          courses: new Map<string, CourseWithRoles>(),
        };
        aircraftCoursesMap.set(aircraft.id, aircraftCourses);
        (aircraft.courseIds ?? []).forEach((courseId) =>
          addRoleCourse(aircraftCourses.courses, certificateMap, courseId, role.id, aircraftCourses.aircraftType),
        );
      });
    });
  });

  // Aircraft types without any course get no column group
  const aircraftCourseGroups = Array.from(aircraftCoursesMap.values())
    .filter(({ courses }) => courses.size > 0)
    .sort((a, b) => a.aircraftType.name.localeCompare(b.aircraftType.name));

  return {
    courses: Array.from(allCoursesMap.values()),
    aircraftCourseGroups: aircraftCourseGroups.map(({ courses }) => sortCoursesByName(Array.from(courses.values()))),
    aircraftTypes: aircraftCourseGroups.map(({ aircraftType }) => aircraftType),
    roles: Array.from(allRolesMap.values()),
    operations: Array.from(allOperationsMap.values()),
  };
};

/**
 * A user's status for one course, from the user's certificate map
 */
const buildCourseStatus = (courseId: string | number, cert: CourseCertificate | undefined): CourseStatus => {
  if (cert && cert.actions.downloadableCertificate) {
    const latestCertificate = cert.actions.downloadableCertificate;
    return {
      course_id: courseId,
      status: latestCertificate.status,
      validUntil: latestCertificate.validUntil ? formatDate(latestCertificate.validUntil) : null,
      buffer: calculateBufferZoneStartDate(latestCertificate.validUntil, cert.bufferZone ?? null),
      days: calculateRemainingDays(latestCertificate.validUntil),
      purchased: cert.actions?.isPurchased ?? false,
      purchasableCourse:
        (latestCertificate.status !== CertificateStatusType.OK &&
          latestCertificate.status !== CertificateStatusType.NEVER_EXPIRE &&
          cert.actions?.purchasableCourse) ||
        undefined,
      courseCertificate: cert,
    };
  }

  // User needs this course but has no certificate - push object with status to maintain alignment
  return {
    course_id: courseId,
    status: CertificateStatusType.MISSING,
    validUntil: null,
    buffer: null,
    days: null,
    purchased: false,
    purchasableCourse: cert?.actions?.purchasableCourse,
    courseCertificate: cert,
  };
};

const CrewOverview: FC<CrewOverviewProps> = (params) => {
  const { t } = useTranslation();
  const { enqueueSnackbar: snack } = useSnackbar();
  const { crew } = useCrew();
  const crewIdParam = params.match.params.crewId;
  const crewId = Number(crewIdParam);
  const isValidCrewId = !isNaN(crewId) && crewId >= 1;

  const [courses, setCourses] = useState<TableCourse[][]>([]);
  const [users, setUsers] = useState<TableUser[]>([]);
  const [noData, setNoData] = useState(false);
  const [certificateMap, setCertificateMap] = useState<CertificateMap>();
  const [searchUsers, setSearchUsers] = useState<number[]>([]);
  const [searchRole, setSearchRole] = useState<number[]>([]);
  const [searchThemes, setSearchThemes] = useState<string[]>([]);
  const [searchCourses, setSearchCourses] = useState<string[]>([]);
  const [searchStatus, setSearchStatus] = useState<string[]>([]);
  const [isLoading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [courseStatus, setCourseStatus] = useState<ReturnType<typeof createSelectableObject>[]>([]);
  const [allRoles, setAllRoles] = useState<ReturnType<typeof createSelectableObject>[]>([]);
  const [courseThemes, setCourseThemes] = useState<ReturnType<typeof createSelectableObject>[]>([]);
  const [userOptions, setUserOptions] = useState<ReturnType<typeof createSelectableObject>[]>([]);
  const [courseOptions, setCourseOptions] = useState<ReturnType<typeof createSelectableObject>[]>([]);
  // Options that exist only through aircraft-specific courses, offered while those are switched on
  const [aircraftCourseOptions, setAircraftCourseOptions] = useState<ReturnType<typeof createSelectableObject>[]>([]);
  const [aircraftCourseThemes, setAircraftCourseThemes] = useState<ReturnType<typeof createSelectableObject>[]>([]);
  const [aircraftTypeOptions, setAircraftTypeOptions] = useState<ReturnType<typeof createSelectableObject>[]>([]);
  const [searchAircraftTypes, setSearchAircraftTypes] = useState<number[]>([]);
  const [includeAircraftCourses, setIncludeAircraftCourses] = useState(false);
  const [purchasedFilter, setPurchasedFilter] = useState(false);
  const { fullscreen, toggleFullscreen } = useContext(DisplayContext);

  const visibleCourseOptions = useMemo(
    () => (includeAircraftCourses ? [...courseOptions, ...aircraftCourseOptions] : courseOptions),
    [includeAircraftCourses, courseOptions, aircraftCourseOptions],
  );
  const visibleCourseThemes = useMemo(
    () => (includeAircraftCourses ? [...courseThemes, ...aircraftCourseThemes] : courseThemes),
    [includeAircraftCourses, courseThemes, aircraftCourseThemes],
  );

  // A course or theme selected while aircraft courses were shown, and reached only through them, is hidden from
  // the filters while they are switched off, so it must not narrow the report then either
  const activeSearchCourses = useMemo(() => {
    if (includeAircraftCourses) return searchCourses;
    const aircraftOnlyCourseIds = new Set(aircraftCourseOptions.map((opt) => String(opt.getId())));
    return searchCourses.filter((courseId) => !aircraftOnlyCourseIds.has(courseId));
  }, [includeAircraftCourses, searchCourses, aircraftCourseOptions]);
  const activeSearchThemes = useMemo(() => {
    if (includeAircraftCourses) return searchThemes;
    const aircraftOnlyThemes = new Set(aircraftCourseThemes.map((opt) => String(opt.getId())));
    return searchThemes.filter((themeId) => !aircraftOnlyThemes.has(themeId));
  }, [includeAircraftCourses, searchThemes, aircraftCourseThemes]);

  // Showing aircraft courses picks the columns, it isn't a filter: clearing the filters leaves it as it is
  const clearFilters = () => {
    setSearchUsers([]);
    setSearchCourses([]);
    setSearchRole([]);
    setSearchThemes([]);
    setSearchStatus([]);
    setSearchAircraftTypes([]);
    setPurchasedFilter(false);
  };

  const hasFilters =
    searchUsers.length > 0 ||
    searchCourses.length > 0 ||
    searchRole.length > 0 ||
    searchThemes.length > 0 ||
    searchStatus.length > 0 ||
    (includeAircraftCourses && searchAircraftTypes.length > 0) ||
    purchasedFilter;
  const [isCapturingScreenshot, setIsCapturingScreenshot] = useState(false);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [isSavingSettings, setIsSavingSettings] = useState(false);
  const theme = useTheme();
  const downLgScreen = useMediaQuery(theme.breakpoints.down("md"));
  const xsScreen = useMediaQuery(theme.breakpoints.down("sm"));

  // Cache refresh functionality
  const { isRefreshing, refreshProgress, oldestCacheTime, refreshKey, dataUpdated, forceRefresh, setOldestCacheTime } =
    useCacheRefresh({
      config: CACHE_REFRESH_CONFIG,
      crewId: isValidCrewId ? crewId : 0,
    });

  useEffect(() => {
    if (!isValidCrewId) return;

    // Reset all states when crew changes
    const isInitialLoad = refreshKey === 0;

    // Always set loading to true when fetching data
    setLoading(true);

    // Reset data arrays on initial load, not on cache refresh
    if (isInitialLoad) {
      setUsers([]);
      setCourses([]);
      setUserOptions([]);
      setCourseOptions([]);
      setAircraftCourseOptions([]);
      setAircraftTypeOptions([]);
    }

    // Simplified: Extract everything from V2 compliance data
    (async () => {
      try {
        // STEP 1: Fetch batch compliance data, users, and default settings in parallel
        const [batchResponse, usersList, defaultSettings] = await Promise.all([
          getComplianceWithCourses(crewId, true, crew?.baseIds),
          getCrewUsers(crewId),
          isInitialLoad ? getDefaultSettings(SettingKey.CREW_COURSE_OVERVIEW) : Promise.resolve(null),
        ]);

        // Set the oldest cache time from batch response
        setOldestCacheTime(batchResponse.cachedAt || null);

        // Load default filters on initial load (crew change), keep current filters on cache refresh
        if (isInitialLoad) {
          if (defaultSettings) {
            const filters = defaultSettings.value;
            if (filters.users) setSearchUsers(filters.users);
            if (filters.roles) setSearchRole(filters.roles);
            if (filters.themes) setSearchThemes(filters.themes);
            if (filters.courses) setSearchCourses(filters.courses);
            if (filters.statuses) setSearchStatus(filters.statuses);
            if (filters.aircraftTypes) setSearchAircraftTypes(filters.aircraftTypes);
            if (filters.includeAircraftCourses !== undefined) setIncludeAircraftCourses(filters.includeAircraftCourses);
            if (filters.purchased !== undefined) setPurchasedFilter(filters.purchased);
          } else {
            setPurchasedFilter(false);
            setIncludeAircraftCourses(false);
            setSearchUsers([]);
            setSearchRole([]);
            setSearchThemes([]);
            setSearchCourses([]);
            setSearchStatus([]);
            setSearchAircraftTypes([]);
          }
        }

        // STEP 3: Combine user info with their compliance data from batch
        const usersWithCompliance: UserWithCompliance[] = usersList
          .map((user: UserCrew) => ({
            user,
            compliance: batchResponse.data[user.id]
              ? { data: batchResponse.data[user.id], cachedAt: batchResponse.cachedAt }
              : null,
          }))
          .filter((item): item is UserWithCompliance => item.compliance !== null);

        // STEP 4: Extract all courses, roles, operations from V2 data
        const {
          courses: extractedCourses,
          aircraftCourseGroups,
          aircraftTypes,
          roles: extractedRoles,
        } = extractDataFromV2Compliance(usersWithCompliance);

        // STEP 5: Group courses by category and sort
        const coursesByCategory: Record<string, CourseWithRoles[]> = {};
        extractedCourses.forEach((course) => {
          const category = course.courseCategory || COMMON.UNCATEGORIZED;
          if (!coursesByCategory[category]) {
            coursesByCategory[category] = [];
          }
          coursesByCategory[category].push(course);
        });

        // Sort categories in desired order
        const categoryOrder = [
          CourseCategoryType.FLIGHT_TRAINING_CHECKING,
          CourseCategoryType.GROUND_TRAINING_CHECKING,
          CourseCategoryType.LICENSES,
          CourseCategoryType.THEORETICAL_TRAINING,
          CourseCategoryType.COMPANY_INSTRUCTOR_EXAMINER,
          COMMON.UNCATEGORIZED,
          CourseCategoryType.SIGN_OFFS,
        ];

        const sortedCoursesGrouped = categoryOrder
          .filter((cat) => coursesByCategory[cat])
          .map((cat) => sortCoursesByName(coursesByCategory[cat]));

        // Add any categories not in the predefined order
        Object.keys(coursesByCategory).forEach((cat) => {
          if (!categoryOrder.includes(cat)) {
            sortedCoursesGrouped.push(sortCoursesByName(coursesByCategory[cat]));
          }
        });

        // Aircraft-specific column groups come after the theme groups; the table shows them when switched on
        const allCourseGroups = [...sortedCoursesGrouped, ...aircraftCourseGroups];

        // STEP 6: Build user rows with statuses from certificateMap
        const userRows: UserRow[] = usersWithCompliance
          .map(({ user, compliance }) => {
            const userName = user.fullname || `User ${user.id}`;
            const statuses: (CourseStatus | null)[] = [];

            // Build a set of courseIds that exist in this user's relationTree
            const userCourseIds = new Set<string>(compliance?.data?.relationTree?.roles?.flatMap(collectRoleCourseIds));
            const userAircraftCourseIds = collectUserAircraftCourseIds(compliance?.data?.relationTree?.roles);

            // For each course in sorted order, get user's status from certificateMap
            allCourseGroups.forEach((coursesInGroup) => {
              coursesInGroup.forEach((course) => {
                // Check if this course is in user's relationTree, under the column's aircraft type for aircraft courses
                const isRequired = course.aircraftType
                  ? Boolean(userAircraftCourseIds.get(course.aircraftType.id)?.has(String(course.id)))
                  : userCourseIds.has(String(course.id));

                // A course the user doesn't need shows an empty cell
                statuses.push(
                  isRequired ? buildCourseStatus(course.id, compliance?.data?.certificateMap[course.id]) : null,
                );
              });
            });

            // Extract user's assigned roles for filtering (with or without positions/operations)
            const userRoles = (compliance?.data?.relationTree?.roles ?? []).map((role) => ({ role_id: role.id }));

            return {
              id: user.id,
              positions: [userName, ...statuses] as [string, ...(CourseStatus | null)[]],
              roles: userRoles,
              aircraftTypeIds: Array.from(userAircraftCourseIds.keys()),
            };
          })
          .sort((a, b) => formatLastFirstName(a.positions[0]).localeCompare(formatLastFirstName(b.positions[0])));

        // STEP 7: Build filter options
        const roleOptions = extractedRoles
          .map((role) => createSelectableObject(role.id, role.name))
          .sort((a, b) => a.getDisplayName().localeCompare(b.getDisplayName()));
        setAllRoles(roleOptions);

        const statusOptions = [
          CertificateStatusType.OK,
          CertificateStatusType.BUFFER,
          CertificateStatusType.EXPIRED,
          CertificateStatusType.MISSING,
          CertificateStatusType.NEVER_EXPIRE,
        ].map((status) => createSelectableObject(status, t(`enums.certificate_status.${status}`)));
        setCourseStatus(statusOptions);

        const createThemeOption = (cat: string) =>
          createSelectableObject(
            cat,
            cat === COMMON.UNCATEGORIZED
              ? t("no_theme")
              : courseCategoryService.getByCode(cat)?.getDisplayName(t) || cat,
          );
        setCourseThemes(Object.keys(coursesByCategory).map(createThemeOption));

        const userAutocompleteOptions = userRows.map((user: any) =>
          createSelectableObject(user.id.toString(), user.positions[0]),
        );
        setUserOptions(userAutocompleteOptions);

        const courseAutocompleteOptions = extractedCourses.map((course) =>
          createSelectableObject(course.id, course.name),
        );
        setCourseOptions(courseAutocompleteOptions);
        const nonAircraftCourseIds = new Set(extractedCourses.map((course) => String(course.id)));

        // Courses and themes reached only through aircraft types; a course of several types is offered once
        const aircraftOnlyCourses = new Map<string, CourseWithRoles>();
        aircraftCourseGroups.flat().forEach((course) => {
          if (!nonAircraftCourseIds.has(String(course.id))) aircraftOnlyCourses.set(String(course.id), course);
        });
        setAircraftCourseOptions(
          Array.from(aircraftOnlyCourses.values()).map((course) => createSelectableObject(course.id, course.name)),
        );
        const aircraftThemes = new Set(
          aircraftCourseGroups.flat().map((course) => course.courseCategory || COMMON.UNCATEGORIZED),
        );
        setAircraftCourseThemes(
          Array.from(aircraftThemes)
            .filter((cat) => !coursesByCategory[cat])
            .map(createThemeOption),
        );
        setAircraftTypeOptions(
          aircraftTypes.map((aircraftType) => createSelectableObject(aircraftType.id, aircraftType.name)),
        );

        // STEP 7: Build merged certificateMap from all users
        const mergedCertificateMap: CertificateMap = {};
        usersWithCompliance.forEach(({ compliance }) => {
          if (compliance?.data?.certificateMap) {
            Object.assign(mergedCertificateMap, compliance.data.certificateMap);
          }
        });

        setCourses(allCourseGroups);
        setUsers(userRows);
        setCertificateMap(mergedCertificateMap);
        setNoData(userRows.length === 0);
        setError(null);
        setInitialLoadComplete(true);
      } catch (err) {
        captureHandledException(err, { feature: "crewCourseOverview", action: "load", crewId });
        setError(t("failed_to_load_crew_overview"));
        setInitialLoadComplete(true);
      } finally {
        setLoading(false);
      }
    })();
  }, [crewId, isValidCrewId, crew?.baseIds, t, refreshKey]);

  // Filter users by search (IDs)
  const filteredUsers = useMemo(() => {
    if (!searchUsers || searchUsers.length === 0) return users;
    return users.filter((user) => searchUsers.includes(user.id));
  }, [users, searchUsers]);

  // Handler for saving current filter settings
  const handleSaveSettings = async () => {
    if (!crewId) return;

    setIsSavingSettings(true);
    try {
      const filterSettings = {
        users: searchUsers,
        roles: searchRole,
        themes: searchThemes,
        courses: searchCourses,
        statuses: searchStatus,
        aircraftTypes: searchAircraftTypes,
        includeAircraftCourses,
        purchased: purchasedFilter,
      };

      await saveDefaultSettings(SettingKey.CREW_COURSE_OVERVIEW, filterSettings);
      snack(t("default_filters_saved"), { variant: "success" });
    } catch (err) {
      captureHandledException(err, { feature: "crewCourseOverview", action: "saveDefaultFilters", crewId });
      snack(t("default_filters_save_failed"), { variant: "error" });
    } finally {
      setIsSavingSettings(false);
    }
  };

  if (!isValidCrewId) {
    return (
      <Typography variant="h6" color="error">
        {t("invalid_crew_id", { crewId: crewIdParam })}
      </Typography>
    );
  }

  if (isLoading) {
    return <Spinner message={t("loading_crew_overview")} />;
  }

  if (error) {
    return (
      <Typography variant="h6" color="error">
        {error}
      </Typography>
    );
  }

  return (
    <div style={{ position: "relative" }}>
      {isCapturingScreenshot && <Spinner message={t("taking_screenshot")} variant="overlay" />}
      {noData && <EmptyState />}
      {!noData && (
        <div
          id="myDiv"
          style={{
            ...(fullscreen && {
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              width: "100vw",
              height: "100vh",
              backgroundColor: "white",
              overflow: "auto",
            }),
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              justifyContent: "space-between",
              alignItems: { xs: "flex-start", md: "center" },
              gap: "10px",
            }}
          >
            <CacheStatusBanner
              cachedAt={oldestCacheTime}
              isRefreshing={isRefreshing}
              refreshProgress={refreshProgress}
              dataUpdated={dataUpdated}
              onRefresh={forceRefresh}
            />
            {!isCapturingScreenshot && (
              <Box
                sx={{
                  display: "flex",
                  gap: "10px",
                  alignItems: "center",
                  flexWrap: "wrap",
                  alignSelf: { xs: "flex-end", md: "auto" },
                }}
              >
                <Button
                  icon={<FilterAltOffIcon />}
                  onClick={clearFilters}
                  disabled={!hasFilters}
                  iconSize={"large"}
                  title={t("clear_all_filters")}
                />
                <Button
                  icon={<SaveIcon />}
                  onClick={handleSaveSettings}
                  disabled={isSavingSettings}
                  iconSize={"large"}
                  title={t("save_default_filters")}
                  variant="outlined"
                />
                <Button
                  icon={
                    <MonetizationOnIcon
                      sx={{
                        color: purchasedFilter ? "#a6e22e" : "inherit",
                      }}
                    />
                  }
                  iconSize={"large"}
                  onClick={() => setPurchasedFilter((prev) => !prev)}
                  title={purchasedFilter ? t("show_all_courses") : t("show_not_purchased_only")}
                  variant={purchasedFilter ? "contained" : "outlined"}
                />
                <Button
                  icon={includeAircraftCourses ? <AirplanemodeInactiveIcon /> : <FlightIcon />}
                  iconSize={"large"}
                  onClick={() => setIncludeAircraftCourses((prev) => !prev)}
                  title={
                    includeAircraftCourses
                      ? t("crew_course_overview.hide_aircraft_courses")
                      : t("crew_course_overview.show_aircraft_courses")
                  }
                  variant={includeAircraftCourses ? "contained" : "outlined"}
                />
                {!downLgScreen && (
                  <Button
                    icon={fullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
                    onClick={() => toggleFullscreen()}
                    iconSize={"large"}
                    title={t("toggle_fullscreen")}
                    variant="outlined"
                  />
                )}
                {!fullscreen && !xsScreen && (
                  <Button
                    icon={<CameraAltIcon />}
                    onClick={() =>
                      takeScreenshot(
                        "myDiv",
                        "crew_course_overview",
                        oldestCacheTime || undefined,
                        () => setIsCapturingScreenshot(true),
                        () => setIsCapturingScreenshot(false),
                      )
                    }
                    iconSize={"large"}
                    title={t("take_screenshot")}
                    variant="outlined"
                  />
                )}
              </Box>
            )}
          </Box>
          <Box
            className="input-wrapper"
            sx={({ spacing }) => ({
              display: "grid",
              gridTemplateColumns: {
                xs: "repeat(1, 1fr)",
                sm: "repeat(2, 1fr)",
                md: includeAircraftCourses ? "repeat(6, 1fr)" : "repeat(5, 1fr)",
              },
              gap: "10px",
              paddingTop: spacing(1),
              paddingBottom: spacing(1),
            })}
          >
            {!isCapturingScreenshot ? (
              <>
                <CheckboxAutocomplete
                  items={userOptions}
                  selected={userOptions.filter((opt) => searchUsers.includes(Number(opt.getId())))}
                  label={t("filter_users")}
                  onChange={(value) => {
                    setSearchUsers(value.map((v) => Number(v.getId())));
                  }}
                />

                <CheckboxAutocomplete
                  items={visibleCourseOptions}
                  selected={visibleCourseOptions.filter((opt) => searchCourses.includes(String(opt.getId())))}
                  label={t("filter_courses")}
                  onChange={(value) => {
                    setSearchCourses(value.map((v) => String(v.getId())));
                  }}
                />

                <CheckboxAutocomplete
                  items={allRoles}
                  selected={allRoles.filter((opt) => searchRole.includes(Number(opt.getId())))}
                  label={t("filter_role")}
                  onChange={(value) => {
                    setSearchRole(value.map((v) => Number(v.getId())));
                  }}
                />

                {includeAircraftCourses && (
                  <CheckboxAutocomplete
                    items={aircraftTypeOptions}
                    selected={aircraftTypeOptions.filter((opt) => searchAircraftTypes.includes(Number(opt.getId())))}
                    label={t("crew_course_overview.filter_aircraft_type")}
                    onChange={(value) => {
                      setSearchAircraftTypes(value.map((v) => Number(v.getId())));
                    }}
                  />
                )}

                <CheckboxAutocomplete
                  items={visibleCourseThemes}
                  selected={visibleCourseThemes.filter((opt) => searchThemes.includes(String(opt.getId())))}
                  label={t("filter_theme")}
                  onChange={(value) => {
                    setSearchThemes(value.map((v) => String(v.getId())));
                  }}
                />

                <CheckboxAutocomplete
                  items={courseStatus}
                  selected={courseStatus.filter((opt) => searchStatus.includes(String(opt.getId())))}
                  label={t("filter_status")}
                  onChange={(value) => {
                    setSearchStatus(value.map((v) => String(v.getId())));
                  }}
                />
              </>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "15px",
                  alignItems: "center",
                  fontSize: "0.9rem",
                }}
              >
                {activeSearchCourses.length > 0 && (
                  <span>
                    <strong>{t("filter_courses")}:</strong>{" "}
                    {visibleCourseOptions
                      .filter((opt) => activeSearchCourses.includes(String(opt.getId())))
                      .map((opt) => opt.getDisplayName())
                      .join(", ")}
                  </span>
                )}
                {searchRole.length > 0 && (
                  <span>
                    <strong>{t("filter_role")}:</strong>{" "}
                    {allRoles
                      .filter((opt) => searchRole.includes(Number(opt.getId())))
                      .map((opt) => opt.getDisplayName())
                      .join(", ")}
                  </span>
                )}
                {includeAircraftCourses && searchAircraftTypes.length > 0 && (
                  <span>
                    <strong>{t("crew_course_overview.filter_aircraft_type")}:</strong>{" "}
                    {aircraftTypeOptions
                      .filter((opt) => searchAircraftTypes.includes(Number(opt.getId())))
                      .map((opt) => opt.getDisplayName())
                      .join(", ")}
                  </span>
                )}
                {activeSearchThemes.length > 0 && (
                  <span>
                    <strong>{t("filter_theme")}:</strong>{" "}
                    {visibleCourseThemes
                      .filter((opt) => activeSearchThemes.includes(String(opt.getId())))
                      .map((opt) => opt.getDisplayName())
                      .join(", ")}
                  </span>
                )}
                {searchStatus.length > 0 && (
                  <span>
                    <strong>{t("filter_status")}:</strong>{" "}
                    {courseStatus
                      .filter((opt) => searchStatus.includes(String(opt.getId())))
                      .map((opt) => opt.getDisplayName())
                      .join(", ")}
                  </span>
                )}
                {purchasedFilter && (
                  <span>
                    <strong>{t("purchased")}:</strong> {t("not_purchased_only")}
                  </span>
                )}
              </Box>
            )}
          </Box>

          <Box
            sx={{
              position: "relative",
              overflow: "hidden",
              "&::after": dataUpdated
                ? {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    height: "3px",
                    background: "linear-gradient(90deg, transparent, rgba(33, 150, 243, 0.95), transparent)",
                    boxShadow: "0 0 20px rgba(33, 150, 243, 0.8), 0 0 40px rgba(33, 150, 243, 0.4)",
                    animation: "sweepDown 1.8s ease-in-out",
                    pointerEvents: "none",
                    zIndex: 10000,
                    "@keyframes sweepDown": {
                      "0%": {
                        top: "0%",
                        opacity: 1,
                      },
                      "100%": {
                        top: "100%",
                        opacity: 0,
                      },
                    },
                  }
                : {},
            }}
          >
            <CrewCourseOverviewTable
              courses={courses}
              users={filteredUsers}
              searchCourses={activeSearchCourses}
              searchRole={searchRole}
              searchThemes={activeSearchThemes}
              searchStatus={searchStatus}
              searchAircraftTypes={searchAircraftTypes}
              includeAircraftCourses={includeAircraftCourses}
              filterPurchased={purchasedFilter}
              initialLoadComplete={initialLoadComplete}
              certificateMap={certificateMap}
              onClearFilters={hasFilters ? clearFilters : undefined}
            />
          </Box>
        </div>
      )}
    </div>
  );
};

export default CrewOverview;
